<!-- eslint-disable no-unused-vars -->
<script setup>
import { ref, computed, inject, watchEffect } from 'vue'
import { useRoute } from 'vue-router'
import { useClassStore } from '@/stores/ClassStore'
import { useCommonStore } from '@/stores/CommonStore'
import { useInstructorStore } from '@/stores/InstructorStore'
import { ChevronLeftIcon } from '@heroicons/vue/24/outline';

import { truncateHtml, cleanMarkup, cleanUrl, stripHtml } from '@/helpers/text'
import { timestampsToDateRange } from '@/helpers/datetime'

import InstructorWorks from './components/InstructorWorks.vue';
import artwork1 from '@/assets/images/masonry/1.jpg';
import artwork2 from '@/assets/images/masonry/2.jpg';
import artwork3 from '@/assets/images/masonry/3.jpg';
import artwork4 from '@/assets/images/masonry/4.jpg';
import artwork5 from '@/assets/images/masonry/5.jpg';
import { useTitle } from '../../hooks/useTitle'
import ShareBar from '@/components/ShareBar.vue'

const instructorArtworks = ref([
  {
    image: artwork1,
    title: 'Sample Artwork Title',
    description: 'Aliqua id fe tempor cupidatat incididunt sint deserunt ut voluptate aute id deserunt nisi.'
  },
  {
    image: artwork2,
    title: 'Sample Artwork Title',
    description: 'Aliqua id fugiat nostrud irure ex duis ea quis id quis ad et. Sunt qui esse pariatur duis deserunt mollit dolore cillum minim tempor enim. Elit aute irure tempor cupidatat incididunt sint deserunt ut voluptate aute id deserunt nisi.'
  },
  {
    image: artwork3,
    title: 'Sample Artwork Title',
    description: 'Aliqua id fugiat nostrud irure exute irurerunt ut voluptate aute id deserunt nisi.'
  },
  {
    image: artwork4,
    title: 'Sample Artwork Title',
    description: 'Aliqua id fugiat nostrud irure ex duis ea quis id quis ad et. Sunt qui esse pariatur duis deserunt mollit dolore cillum minim tempor enim. Elit aute irure tempor cupidatat incididunt sint deserunt ut voluptate aute id deserunt nisi.'
  },
  {
    image: artwork5,
    title: 'Sample Artwork Title',
    description: 'Aliquaor cupidatat ideserunt nisi.'
  },
])

const uploadedArtworks = ref([
  {
    isUploading: true,
    filename: 'filename.jpg',
    image: artwork1,
    progress: 0,
  },
  {
    hasError: true,
    filename: 'filename.pdf',
    errorMsg: 'wrong file type'
  },
  {
    isFullyUploaded: true,
    image: artwork3,
    title: '',
    description: ''
  },
])

const siteConfig = inject('siteConfig')

const commonStore = useCommonStore()

const classStore = useClassStore()
const instructorStore = useInstructorStore()

const route = useRoute()

const instructor = computed(() => {
  const inst = instructorStore.instructorsById[route.params.instructorId]
  return inst
})

const instructorBio = ref(stripHtml(instructor.value?.bio))

// const instructor = ref({
//   id: 'DenUCtWH89',
//   image: instructor1Img,
//   name: 'Clinton Mcclure',
//   job: 'Art director',
//   description:
//     'Lorem ipsum dolor sit amet consectetur adipisicing elit. Eaque autem dolorum dolore amet incidunt quibusdam beatae a quos illum',
//   social: {
//     twitter: 'https://www.twitter.com',
//     facebook: 'https://www.facebook.com',
//     dribbble: 'https://www.dribbble.com',
//   },
// })

const classes = computed(() => {
  const instructorId = parseInt(route.params.instructorId)
  return classStore.openClasses
    .filter((cls) => {
      return (
        cls.instructorId === instructorId || cls.instructor2Id == instructorId
      )
    })
})

const openedLighbox = ref(false)

const editProfileModal = ref(false)
const toggleEditProfileModal = () => {
  document.body.classList.toggle('!overflow-hidden')
  editProfileModal.value = !editProfileModal.value
}

const deleteArtwork = (array, index) => {
  array.splice(index, 1)
}

const editArtworkModal = ref({
  isOpened: false,
  artwork: { image: '', title: '', description: ''},
});
const openEditArtworkModal = (artwork) => {
  editArtworkModal.value.isOpened = true;
  editArtworkModal.value.artwork = artwork;
};
const closeEditArtworkModal = (artwork) => {
  editArtworkModal.value.isOpened = false;
  editArtworkModal.value.artwork = { image: '', title: '', description: ''};
};

const dragging = ref(false);
const isFileUploading = ref(false);
const onFileChange = (e) => {
  if (e.target.files[0].name) {
    isFileUploading.value = true;
  }
}

watchEffect(() => {
  if (isFileUploading.value) {
    uploadedArtworks.value.forEach(artwork => {
      if (artwork.isUploading) {
        const artworkInterval = setInterval(() => {
          artwork.progress += 1
          if (artwork.progress >= 100) {
            clearInterval(artworkInterval)
            artwork.isUploading = false
            artwork.isFullyUploaded = true
            artwork.title = ''
            artwork.description = ''
          }
        }, 100)
      }
    })
  }
})

const { prefixTitle } = useTitle();
watchEffect(() => {
  if (instructor.value) {
    prefixTitle(instructor.value.fullName)
  }
})
</script>

<template>
  <div>
    <div class="container">
      <section class="instructor-profile" v-if="instructor">
        <template v-if="!openedLighbox || commonStore.windowInnerWidth > 768">
          <div class="page-header">
            <router-link 
              to="/instructors" 
              class="go-back-link btn-accent-1 small-bold"
            >
              <ChevronLeftIcon class="icon" /> Go Back
            </router-link>
            <!-- <button class="outlined-accent-1" @click="toggleEditProfileModal">Edit this profile</button> -->
            <ShareBar/>
          </div>
          <div class="instructor-info">
            <img
              v-if="instructor.imageUrl"
              :src="instructor.imageUrl"
              :alt="`${instructor.fullName} Profile Image`"
            />
            <img
              v-if="!instructor.imageUrl"
              :src="`/sites/${siteConfig.key}/placeholder-avatar.jpg`"
              :alt="`${instructor.fullName} Profile Image`"
            />
            <h1 class="page-heading" v-html="instructor.fullName"></h1>
            <!-- <span v-html="instructor.job"></span> -->
            <p class="markup" v-if="instructor.bio" v-html="cleanMarkup(instructor.bio)"></p>
            <ul>
              <li v-if="instructor.website">
                <a
                  :href="cleanUrl(instructor.website)"
                  class="website"
                  target="_blank"
                >
                  <Icon icon="akar-icons:link-chain" class="icon" />
                </a>
              </li>
              <li v-if="instructor.social?.twitter">
                <a
                  :href="instructor.social.twitter"
                  class="twitter"
                  target="_blank"
                >
                  <Icon icon="akar-icons:twitter-fill" class="icon" />
                </a>
              </li>
              <li v-if="instructor.social?.facebook">
                <a
                  :href="instructor.social.facebook"
                  class="facebook"
                  target="_blank"
                >
                  <Icon icon="brandico:facebook" class="icon" />
                </a>
              </li>
              <li v-if="instructor.social?.dribbble">
                <a
                  :href="instructor.social.dribbble"
                  class="dribbble"
                  target="_blank"
                >
                  <Icon icon="akar-icons:dribbble-fill" class="icon" />
                </a>
              </li>
            </ul>
          </div>
          <div class="instructor-classes" v-if="instructor && classes.length">
            <div class="section-info">
              <h2 class="page-heading">{{ instructor.firstName }}'s Classes</h2>

              <!-- <p class="page-description">
                Lorem ipsum dolor sit amet consectetur adipisicing elit. Repudiandae
                voluptatum iste vitae optio aperiam? Culpa expedita temporibus
                maiores accusamus numquam, voluptatum dicta quae a! Sapiente beatae
                consequatur quaerat alias eaque?
              </p> -->

              <!-- <button class="view-all body-bold"> View All </button> -->
            </div>

            <div class="classes-box">
              <div
                class="class-card"
                v-for="(cls, index) in classes"
                :key="index"
              >
                <router-link
                  :to="{
                    name: 'Class Details',
                    params: { classId: cls.catalogId },
                  }"
                >
                  <div class="class-image">
                    <img :src="cls.imageUrl" />
                    <div class="image-overlay">
                      <h5 class="large-bold" v-html="cls.category"></h5>
                      <span class="extra-small" v-html="cls.type"></span>
                    </div>
                  </div>
                </router-link>

                <h2 class="xlarge-text my-2" v-html="cls.shortTitle"></h2>

                <div class="class-info">
                  <p class="small-regular">
                    <Icon icon="ant-design:user-outlined" class="icon" />
                    <span v-html="cls.instructorSummary"></span>
                  </p>
                  <p class="small-regular">
                    <Icon icon="ci:calendar" class="icon" />
                    <span
                      v-html="
                        `${cls.classDaySummary}, ${timestampsToDateRange(
                          cls.startDate,
                          cls.endDate
                        )}`
                      "
                    ></span>
                  </p>
                  <p class="small-regular">
                    <Icon icon="carbon:location" class="icon" />
                    <span v-html="cls.location"></span>
                  </p>
                  <p class="small-regular">
                    <Icon icon="carbon:meter-alt" class="icon" />
                    <span v-html="cls.classLevel"></span>
                  </p>

                  <div class="class-price">
                    <h3 class="main-price heading-3">
                      &dollar;{{ cls.nonMemberCostFmt }}
                    </h3>
                    <h4
                      class="members-price body-bold"
                      v-if="cls.nonMemberCostFmt != cls.memberCostFmt"
                    >
                      &dollar;{{ cls.memberCostFmt }}
                      <span class="small-regular">(members)</span>
                    </h4>
                  </div>

                  <a
                    v-if="cls.udef1 && cls.udef1.includes('http')"
                    :href="cls.udef1"
                    class="btn-accent-1 btn-register xl:large-regular"
                  >
                    Register
                  </a>
                  <router-link
                    v-else
                    :to="{
                      name: 'Class Details',
                      params: { classId: cls.catalogId },
                    }"
                    class="btn-accent-1 btn-register xl:large-regular"
                  >
                    {{ siteConfig.copy?.classes?.learnMoreLabel || 'Learn More' }}
                  </router-link>
                </div>
              </div>
            </div>
          </div>

          <div class="edit-profile-modal" v-if="editProfileModal">
            <div class="modal-body">
              <header class="modal-header">
                <h2 class="heading-3">Edit Instructor Profile</h2>
                <div class="modal-actions">
                  <button class="outlined-accent-1" @click="toggleEditProfileModal"> Cancel </button>
                  <button class="btn-accent-1" @click="toggleEditProfileModal"> Save </button>
                </div>
              </header>
              <div class="modal-content">
                <div class="instructor-image">
                  <img :src="instructor.imageUrl" :alt="instructor.fullName + 'Profile pic'" />
                  <button class="outlined-accent-1 small-bold">Change</button>
                </div>
                <div class="modal-fields">
                  <div class="form-row">
                    <label class="input-component">
                      <strong>Name</strong>
                      <input type="text" placeholder="Name" class="form-control" v-model="instructor.fullName" />
                    </label>
                    <!-- This was previously instructor job which you comment it already -->
                    <!-- <label class="input-component">
                      <strong>Title</strong>
                      <input type="text" placeholder="Title" class="form-control" />
                    </label> -->
                  </div>
                  <label class="input-component textarea-input">
                    <strong>Bio</strong>
                    <textarea rows="5" class="form-control py-2" placeholder="Your bio..." v-model="instructorBio"></textarea>
                  </label>
                  <label class="input-component">
                    <strong>Personal Website <span class="text-secondary-text font-normal">(optional)</span> </strong>
                    <input type="url" placeholder="www.example.com" class="form-control" />
                  </label>
                  <label class="input-component">
                    <strong>Facebook URL <span class="text-secondary-text font-normal">(optional)</span> </strong>
                    <input type="url" placeholder="https://www.facebook.com/sample" class="form-control" />
                  </label>
                  <label class="input-component">
                    <strong>Instagram URL <span class="text-secondary-text font-normal">(optional)</span> </strong>
                    <input type="url" placeholder="https://www.instagram.com/sample" class="form-control" />
                  </label>
                  <label class="input-component">
                    <strong>LinkedIn URL <span class="text-secondary-text font-normal">(optional)</span> </strong>
                    <input type="url" placeholder="https://www.linkedin.com/sample" class="form-control" />
                  </label>
                  <label class="input-component">
                    <strong>Twitter URL <span class="text-secondary-text font-normal">(optional)</span> </strong>
                    <input type="url" placeholder="https://www.twitter.com/sample" class="form-control" />
                  </label>
                </div>
                <div class="instructor-artworks">
                  <h4 class="font-bold mb-5">Artwork</h4>

                  <div class="upload-artwork">
                    <label class="dragzone">
                      <input
                        @dragenter="dragging = true"
                        @dragleave="dragging = false"
                        @dragover="dragging = true"
                        @drop="dragging = false"
                        type="file"
                        name="UploadInput"
                        class="file-drop"
                        @change="onFileChange"
                      />
                      <div class="upload-box" :class="{ dragging: dragging }">
                        <h2 class="heading-3">Drop files here or click to upload</h2>
                      </div>
                      <button class="outlined-accent-1 upload-btn">Upload Image(s)</button>
                    </label>
                    <div class="uploaded-files mt-7" v-if="isFileUploading">
                      <div class="artwork-box" v-for="artwork, index in uploadedArtworks" :key="index">
                        <div class="left-column">
                          <img src="@/assets/images/no-preview.png" v-if="artwork.hasError" />
                          <img :src="artwork.image" v-else />
                          <div class="box-actions">
                            <button class="btn edit" @click="openEditArtworkModal(artwork)" v-if="artwork.isFullyUploaded">
                              <Icon icon="heroicons-outline:pencil" class="icon" /> Edit
                            </button>
                            <button class="btn delete" @click="deleteArtwork(uploadedArtworks, index)" v-if="artwork.isFullyUploaded || artwork.hasError" :class="{ 'md:mx-auto !text-red-600': artwork.hasError }">
                              <Icon icon="heroicons-outline:trash" class="icon" /> Delete
                            </button>
                          </div>
                        </div>
                        <div class="right-column">
                          <template v-if="artwork.isUploading">
                            <h3 class="font-bold mb-2" v-html="artwork.filename"></h3>
                            <div class="uploading-progress">
                              <div class="progress-bar">
                                <span class="progress-bar-indicator" :style="{ width: artwork.progress + '%' }"></span>
                              </div>
                              <span class="progress-percent small-regular" v-html="artwork.progress + '%'"></span>
                            </div>
                          </template>
                          <template v-if="artwork.hasError">
                            <h3 class="font-bold mb-2" v-html="artwork.filename"></h3>
                            <p class="small-regular text-red-600">Error: {{ artwork.errorMsg }} </p>
                          </template>
                          <template v-if="artwork.isFullyUploaded">
                            <h3 class="font-bold mb-2" v-html="artwork.title || 'Artwork Title'" :class="{ 'text-gray-300': !artwork.title }"></h3>
                            <p class="small-regular" v-html="artwork.description || 'Artwork description'" :class="{ 'text-gray-300': !artwork.description }"></p>
                          </template>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="artworks-list">
                    <div class="artwork-box" v-for="artwork, index in instructorArtworks" :key="index">
                      <div class="left-column">
                        <img :src="artwork.image" />
                        <div class="box-actions">
                          <button class="btn edit" @click="openEditArtworkModal(artwork)"> <Icon icon="heroicons-outline:pencil" class="icon" /> Edit </button>
                          <button class="btn delete" @click="deleteArtwork(instructorArtworks, index)"> <Icon icon="heroicons-outline:trash" class="icon" /> Delete </button>
                        </div>
                      </div>
                      <div class="right-column">
                        <h3 class="font-bold mb-2" v-html="artwork.title"></h3>
                        <p class="small-regular" v-html="artwork.description"></p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="edit-artwork-modal" v-if="editArtworkModal.isOpened">
                <header class="artwork-header">
                  <button class="btn-back" @click="closeEditArtworkModal"> <Icon icon="heroicons-outline:chevron-left" /> </button>
                  <h2 class="heading-3">Edit Info</h2>
                </header>
                <div class="artwork-content">
                  <img :src="editArtworkModal.artwork.image" class="artwork-image" />
                  <label class="input-component">
                    <strong>Artwork Title</strong>
                    <input type="text" placeholder="Your Artwork Title" class="form-control" v-model="editArtworkModal.artwork.title" />
                  </label>
                  <label class="input-component textarea-input">
                    <strong>Artwork Description</strong>
                    <textarea rows="5" class="form-control py-2" placeholder="Describe Your Jewelry" v-model="editArtworkModal.artwork.description"></textarea>
                  </label>
                  <div class="artwork-actions">
                    <button class="outlined-accent-1" @click="closeEditArtworkModal">Cancel</button>
                    <button class="btn-accent-1" @click="closeEditArtworkModal">Save</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
        <!-- <InstructorWorks
          :instructor="instructor"
          @openLightbox="openedLighbox = true"
          @closeLightbox="openedLighbox = false"
        /> -->
      </section>
    </div>
    <div class="edit-profile-modal" v-if="editProfileModal">
      <div class="modal-body">
        <header class="modal-header">
          <h2 class="heading-3">Edit Instructor Profile</h2>
          <div class="modal-actions">
            <button class="outlined-accent-1" @click="toggleEditProfileModal"> Cancel </button>
            <button class="btn-accent-1" @click="toggleEditProfileModal"> Save </button>
          </div>
        </header>
        <div class="modal-content">
          <div class="instructor-image">
            <img :src="instructor.image" :alt="instructor.name + 'Profile pic'" />
            <button class="outlined-accent-1 small-bold">Change</button>
          </div>
          <div class="modal-fields">
            <div class="form-row">
              <label class="input-component">
                <strong>Name</strong>
                <input type="text" placeholder="Name" class="form-control" v-model="instructor.name" />
              </label>
              <label class="input-component">
                <strong>Title</strong>
                <input type="text" placeholder="Title" class="form-control" v-model="instructor.title" />
              </label>
            </div>
            <label class="input-component textarea-input">
              <strong>Bio</strong>
              <textarea rows="5" class="form-control py-2" placeholder="Your bio..." v-model="instructor.bio"></textarea>
            </label>
            <label class="input-component">
              <strong>Personal Website <span class="text-secondary-text font-normal">(optional)</span> </strong>
              <input type="url" placeholder="www.example.com" class="form-control" />
            </label>
            <label class="input-component">
              <strong>Facebook URL <span class="text-secondary-text font-normal">(optional)</span> </strong>
              <input type="url" placeholder="https://www.facebook.com/sample" class="form-control" />
            </label>
            <label class="input-component">
              <strong>Instagram URL <span class="text-secondary-text font-normal">(optional)</span> </strong>
              <input type="url" placeholder="https://www.instagram.com/sample" class="form-control" />
            </label>
            <label class="input-component">
              <strong>LinkedIn URL <span class="text-secondary-text font-normal">(optional)</span> </strong>
              <input type="url" placeholder="https://www.linkedin.com/sample" class="form-control" />
            </label>
            <label class="input-component">
              <strong>Twitter URL <span class="text-secondary-text font-normal">(optional)</span> </strong>
              <input type="url" placeholder="https://www.twitter.com/sample" class="form-control" />
            </label>
          </div>
          <div class="instructor-artworks">
            <h4 class="font-bold mb-5">Artwork</h4>

            <div class="upload-artwork">
              <label class="dragzone">
                <input
                  @dragenter="dragging = true"
                  @dragleave="dragging = false"
                  @dragover="dragging = true"
                  @drop="dragging = false"
                  type="file"
                  name="UploadInput"
                  class="file-drop"
                  @change="onFileChange"
                />
                <div class="upload-box" :class="{ dragging: dragging }">
                  <h2 class="heading-3">Drop files here or click to upload</h2>
                </div>
                <button class="outlined-accent-1 upload-btn">Upload Image(s)</button>
              </label>
              <div class="uploaded-files mt-7" v-if="isFileUploading">
                <div class="artwork-box" v-for="artwork, index in uploadedArtworks" :key="index">
                  <div class="left-column">
                    <img src="@/assets/images/no-preview.png" v-if="artwork.hasError" />
                    <img :src="artwork.image" v-else />
                    <div class="box-actions">
                      <button class="btn edit" @click="openEditArtworkModal(artwork)" v-if="artwork.isFullyUploaded">
                        <Icon icon="heroicons-outline:pencil" class="icon" /> Edit
                      </button>
                      <button class="btn delete" @click="deleteArtwork(uploadedArtworks, index)" v-if="artwork.isFullyUploaded || artwork.hasError" :class="{ 'md:mx-auto !text-red-600': artwork.hasError }">
                        <Icon icon="heroicons-outline:trash" class="icon" /> Delete
                      </button>
                    </div>
                  </div>
                  <div class="right-column">
                    <template v-if="artwork.isUploading">
                      <h3 class="font-bold mb-2" v-html="artwork.filename"></h3>
                      <div class="uploading-progress">
                        <div class="progress-bar">
                          <span class="progress-bar-indicator" :style="{ width: artwork.progress + '%' }"></span>
                        </div>
                        <span class="progress-percent small-regular" v-html="artwork.progress + '%'"></span>
                      </div>
                    </template>
                    <template v-if="artwork.hasError">
                      <h3 class="font-bold mb-2" v-html="artwork.filename"></h3>
                      <p class="small-regular text-red-600">Error: {{ artwork.errorMsg }} </p>
                    </template>
                    <template v-if="artwork.isFullyUploaded">
                      <h3 class="font-bold mb-2" v-html="artwork.title || 'Artwork Title'" :class="{ 'text-gray-300': !artwork.title }"></h3>
                      <p class="small-regular" v-html="artwork.description || 'Artwork description'" :class="{ 'text-gray-300': !artwork.description }"></p>
                    </template>
                  </div>
                </div>
              </div>
            </div>

            <div class="artworks-list">
              <div class="artwork-box" v-for="artwork, index in instructorArtworks" :key="index">
                <div class="left-column">
                  <img :src="artwork.image" />
                  <div class="box-actions">
                    <button class="btn edit" @click="openEditArtworkModal(artwork)"> <Icon icon="heroicons-outline:pencil" class="icon" /> Edit </button>
                    <button class="btn delete" @click="deleteArtwork(instructorArtworks, index)"> <Icon icon="heroicons-outline:trash" class="icon" /> Delete </button>
                  </div>
                </div>
                <div class="right-column">
                  <h3 class="font-bold mb-2" v-html="artwork.title"></h3>
                  <p class="small-regular" v-html="artwork.description"></p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="edit-artwork-modal" v-if="editArtworkModal.isOpened">
          <header class="artwork-header">
            <button class="btn-back" @click="closeEditArtworkModal"> <Icon icon="heroicons-outline:chevron-left" /> </button>
            <h2 class="heading-3">Edit Info</h2>
          </header>
          <div class="artwork-content">
            <img :src="editArtworkModal.artwork.image" class="artwork-image" />
            <label class="input-component">
              <strong>Artwork Title</strong>
              <input type="text" placeholder="Your Artwork Title" class="form-control" v-model="editArtworkModal.artwork.title" />
            </label>
            <label class="input-component textarea-input">
              <strong>Artwork Description</strong>
              <textarea rows="5" class="form-control py-2" placeholder="Describe Your Jewelry" v-model="editArtworkModal.artwork.description"></textarea>
            </label>
            <div class="artwork-actions">
              <button class="outlined-accent-1" @click="closeEditArtworkModal">Cancel</button>
              <button class="btn-accent-1" @click="closeEditArtworkModal">Save</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
@import url('./index.pcss')
</style>
